/**
 * The Crew screen's URL state, as pure functions.
 *
 * Extracted from the component so the two rules below are testable, because
 * both were got wrong the first time and neither is visible in a render:
 *
 *  - **A patch is one write.** react-router's functional updater does not
 *    queue: two `setSearchParams` calls in one handler both recompute from the
 *    render's params and the second discards the first. "Clear filter" issued
 *    two and cleared only the search, leaving the tile selected — and the CSV
 *    export then wrote whatever that stale filter still selected.
 *  - **"all" is only a default for the keys whose default it is.** Treating it
 *    as a global delete sentinel meant typing "all" into the search box deleted
 *    `q` and reset the input mid-word.
 */

/**
 * Value that means "not set", per key. Absent keys have no default.
 *
 * `sort`/`dir` default to the order the API already returns — newest period
 * first — so the untouched view puts nothing in the URL and the default is
 * stated in one place rather than implied by the server's `ORDER BY`.
 */
export const PARAM_DEFAULTS: Readonly<Record<string, string>> = {
  filter: "all",
  stint: "all",
  role: "all",
  sort: "started",
  dir: "desc",
};

/**
 * Normalise one value for the URL. `null` when it should not appear at all.
 *
 * `q` is deliberately absent from `PARAM_DEFAULTS`, so a search for the literal
 * text "all" survives.
 */
export function paramValue(key: string, value: string | null): string | null {
  if (value === null || value === "") return null;
  return value === PARAM_DEFAULTS[key] ? null : value;
}

/** Apply a whole patch to a params object. `null` deletes. */
export function applyPatch(
  prev: URLSearchParams,
  patch: Readonly<Record<string, string | null>>,
): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const [key, value] of Object.entries(patch)) {
    if (value === null) next.delete(key);
    else next.set(key, value);
  }
  return next;
}

/** Everything "Clear filter" removes on the Roster tab. */
export const ROSTER_CLEAR = { filter: null, q: null } as const;

/**
 * Everything "Clear filters" removes on the Reports tab.
 *
 * Sort is deliberately not in here. It is not a filter — it hides nothing — and
 * a "Clear filters" that also threw away the column you were reading by would
 * be a surprise, not a convenience.
 */
export const REPORTS_CLEAR = { stint: null, role: null, q: null } as const;

/** Columns the Reports table can be ordered by. */
export const SORT_KEYS = ["name", "role", "started", "offboarded"] as const;
export type SortKey = (typeof SORT_KEYS)[number];
export type SortDir = "asc" | "desc";

/**
 * The direction a column opens on when it is first clicked.
 *
 * Text reads A→Z; dates read newest-first. Clicking "Started" and landing on
 * the oldest period in the project is the kind of thing that reads as broken
 * even though it is a defensible default, so the two families differ.
 */
const FIRST_DIR: Readonly<Record<SortKey, SortDir>> = {
  name: "asc",
  role: "asc",
  started: "desc",
  offboarded: "desc",
};

/**
 * What the URL should become when a column header is clicked.
 *
 * Two states, not three: the list always has *some* order, so "unsorted" is not
 * a thing a user can see and cycling through it would just look like a click
 * that did nothing.
 */
export function nextSort(
  current: { sort: SortKey; dir: SortDir },
  clicked: SortKey,
): { sort: SortKey; dir: SortDir } {
  if (current.sort === clicked) {
    return { sort: clicked, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return { sort: clicked, dir: FIRST_DIR[clicked] };
}

/** Narrow an untrusted `?sort=` to a column, falling back to the default. */
export function readSortKey(value: string | null): SortKey {
  return (SORT_KEYS as readonly string[]).includes(value ?? "")
    ? (value as SortKey)
    : (PARAM_DEFAULTS.sort as SortKey);
}

/** Narrow an untrusted `?dir=` to a direction, falling back to the default. */
export function readSortDir(value: string | null): SortDir {
  return value === "asc" || value === "desc"
    ? value
    : (PARAM_DEFAULTS.dir as SortDir);
}
